/**
 * TenantReadOnlyContext (QM8D Rev.1000 · FIX-02).
 *
 * Globaler Frontend-State, der gesetzt wird, sobald das Backend den
 * Header ``X-Tenant-ReadOnly: true`` mit einem Response zurueckliefert
 * (siehe ``backend/middleware/read_only_mode.py``). Wird per
 * Axios-Response-Interceptor aktualisiert (``lib/axiosClient.js``).
 *
 * Konsumenten:
 * - ``TenantReadOnlyBanner`` zeigt ein Top-Banner solange ``readOnly === true``.
 * - Beliebige Komponenten koennen ``useTenantReadOnly()`` nutzen, um
 *   z.B. Save-Buttons zu disablen.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const TenantReadOnlyContext = createContext({
  readOnly: false,
  source: null,
  lastSeenAt: null,
  setReadOnlyState: () => {},
  clearReadOnlyState: () => {},
});

const _listeners = new Set();

/**
 * Globaler Push-Channel — Axios-Interceptor ruft ``notifyReadOnlyChange``
 * an, ohne React-Context-Bindung zu kennen.
 */
export function notifyReadOnlyChange(payload) {
  _listeners.forEach((fn) => {
    try {
      fn(payload);
    } catch {
      /* ignore listener errors */
    }
  });
}

export function TenantReadOnlyProvider({ children }) {
  const [state, setState] = useState({
    readOnly: false,
    source: null,
    lastSeenAt: null,
  });

  const setReadOnlyState = useCallback((next) => {
    setState((prev) => {
      if (prev.readOnly === next.readOnly && prev.source === next.source) {
        return { ...prev, lastSeenAt: next.lastSeenAt || prev.lastSeenAt };
      }
      return {
        readOnly: !!next.readOnly,
        source: next.source || null,
        lastSeenAt: next.lastSeenAt || new Date().toISOString(),
      };
    });
  }, []);

  const clearReadOnlyState = useCallback(() => {
    setState({ readOnly: false, source: null, lastSeenAt: null });
  }, []);

  useEffect(() => {
    const listener = (payload) => {
      if (!payload) return;
      if (payload.readOnly) {
        setReadOnlyState(payload);
      } else {
        clearReadOnlyState();
      }
    };
    _listeners.add(listener);
    return () => {
      _listeners.delete(listener);
    };
  }, [setReadOnlyState, clearReadOnlyState]);

  return (
    <TenantReadOnlyContext.Provider
      value={{ ...state, setReadOnlyState, clearReadOnlyState }}
    >
      {children}
    </TenantReadOnlyContext.Provider>
  );
}

export function useTenantReadOnly() {
  return useContext(TenantReadOnlyContext);
}

export default TenantReadOnlyContext;
